import { allPass, complement, either, none } from "ramda";
import React from "react";
import Info from "components/chrome/main_view/info";
import {
  WorkforceBadPermissions,
  WorkforceGettingStarted,
} from "components/chrome/main_view/info_messages";
import PermissionMissingInfo from "components/users/permission_missing_info";

import {
  BREADCRUMB_PERMISSIONS,
  hasAnalyticsPermission,
  hasBreadcrumbPermission,
} from "helpers/chrome/locations";
import type { IBrandGroup } from "helpers/transforms/entities";
import type { IEntity } from "interfaces/app";
import { isAuditLogFeatureEnabled } from "navigation/audit_logs_feature_gate";
import type { IVerifyPermissionsExtras } from "types/navigation";

export const userListsPermission = ({ stores }: IBrandGroup) => {
  if (
    none(
      either(
        hasBreadcrumbPermission(BREADCRUMB_PERMISSIONS.USERS),
        hasAnalyticsPermission("viewMerchantUsers")
      ),
      stores
    )
  ) {
    return <PermissionMissingInfo pageName="users and roles" />;
  }
};

export const userRolesBCPermission = ({ stores }: IBrandGroup) => {
  if (
    none(hasBreadcrumbPermission(BREADCRUMB_PERMISSIONS.ROLE_ACCESS), stores)
  ) {
    return <PermissionMissingInfo pageName="users and roles" />;
  }
};

export const userRolesAnalyticsPermission = ({ stores }: IBrandGroup) => {
  if (none(hasAnalyticsPermission("viewMerchantUsers"), stores)) {
    return <PermissionMissingInfo pageName="users and roles" />;
  }
};

export const workforcePermission = (
  _: IBrandGroup,
  store: IEntity | undefined
) => {
  if (!store) {
    return;
  }
  if (
    !allPass([
      hasBreadcrumbPermission(BREADCRUMB_PERMISSIONS.USERS),
      hasBreadcrumbPermission(BREADCRUMB_PERMISSIONS.ROLE_ACCESS),
    ])(store)
  ) {
    // User lacks either USERS or ROLE_ACCESS permission
    return (
      <Info title="Additional Permissions Required">
        <WorkforceBadPermissions />
      </Info>
    );
  }
  if (
    allPass([
      hasBreadcrumbPermission(BREADCRUMB_PERMISSIONS.USERS),
      hasBreadcrumbPermission(BREADCRUMB_PERMISSIONS.ROLE_ACCESS),
      complement(hasBreadcrumbPermission(BREADCRUMB_PERMISSIONS.WORKFORCE)),
    ])(store)
  ) {
    // User mostly has correct permissions but lacks workforce permission
    return (
      <Info title="Getting Started">
        <WorkforceGettingStarted />
      </Info>
    );
  }
};

export const BrandMenuPermission = ({ stores }: IBrandGroup) => {
  if (none(hasBreadcrumbPermission(BREADCRUMB_PERMISSIONS.MENU), stores)) {
    return <PermissionMissingInfo pageName="Brand Menus" />;
  }
};

/**
 * Blocks main-view content (including deep links) when LaunchDarkly or Breadcrumb
 * reports checks fail — same rules as {@link isAuditLogFeatureEnabled}.
 */
export function auditLogsVerifyPermissions(
  _brand: IBrandGroup,
  _store: IEntity | undefined,
  extras?: IVerifyPermissionsExtras
): JSX.Element | undefined {
  if (!extras || !isAuditLogFeatureEnabled(extras.featureFlags, _store)) {
    return <PermissionMissingInfo pageName="audit logs" />;
  }
  return undefined;
}
